import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Upload,
  ArrowUpRight,
  Calendar,
  Play,
  Pause,
  Square,
  Bell,
  Mail
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { PAGEPATHS } from 'components/Common/Constant';
import axiosInstance from 'components/Common/ApiManagement/axiosInstance';
import { ApiNames } from 'components/Common/ApiManagement/apiServices';
import { useSelector } from 'react-redux';

const MainDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.users.users);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(5048); // 01:24:08
  const [scansData, setScansData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScansData();
  }, [user]);

  const fetchScansData = async () => {
    try {
      setLoading(true);
      const concat = user?.userId ? `?userId=${user.userId}` : '';
      const response = await axiosInstance.get(ApiNames.LIST_SCANS + concat);
      setScansData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching scans:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats from real scan data
  const totalScans = scansData.length;
  const completedScans = scansData.filter(s => s.scanStatus === 'SUCCESS').length;
  const runningScans = scansData.filter(s => s.scanStatus === 'IN_PROGRESS').length;
  const pendingScans = scansData.filter(s => s.scanStatus === 'PENDING' || s.scanStatus === 'FAILED').length;

  const statsData = [
    {
      title: 'Total Projects',
      value: totalScans.toString(),
      subtitle: 'Increased from last month',
      icon: '📊',
      isPrimary: true
    },
    {
      title: 'Ended Projects',
      value: completedScans.toString(),
      subtitle: 'Increased from last month',
      icon: '📈'
    },
    {
      title: 'Running Projects',
      value: runningScans.toString(),
      subtitle: 'Increased from last month',
      icon: '⚡'
    },
    {
      title: 'Pending Project',
      value: pendingScans.toString(),
      subtitle: 'On Discuss',
      icon: '⏱️'
    }
  ];

  const analyticsData = [
    { day: 'M', value: 30 },
    { day: 'T', value: 85 },
    { day: 'W', value: 70 },
    { day: 'T', value: 95 },
    { day: 'F', value: 40 },
    { day: 'S', value: 25 },
    { day: 'S', value: 20 }
  ];

  const teamMembers = [
    {
      name: user?.username || 'Current User',
      avatar: '👤',
      task: 'Managing Compliance Scans',
      status: 'Completed',
      statusColor: 'green'
    },
    {
      name: 'Security Team',
      avatar: '🛡️',
      task: 'Integrate User Authentication System',
      status: 'In Progress',
      statusColor: 'yellow'
    },
    {
      name: 'Compliance Team',
      avatar: '📋',
      task: 'Develop Search and Filter Functionality',
      status: 'Pending',
      statusColor: 'red'
    },
    {
      name: 'Audit Team',
      avatar: '🔍',
      task: 'Responsive Layout for Homepage',
      status: 'In Progress',
      statusColor: 'yellow'
    }
  ];

  const recentProjects = scansData.slice(0, 5).map((scan, index) => ({
    icon: ['⚡', '🌊', '📊', '⚡', '🧪'][index] || '📄',
    name: scan.Domain || scan.Url?.substring(0, 30) || 'Compliance Scan',
    dueDate: scan.scanStartTime ? new Date(scan.scanStartTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set',
    color: ['blue', 'cyan', 'purple', 'yellow', 'indigo'][index] || 'blue',
    scanId: scan._id
  }));

  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setTimerSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const progressData = [
    { name: 'Completed', value: completedScans || 41, color: '#3b82f6' },
    { name: 'In Progress', value: runningScans || 30, color: '#1d4ed8' },
    { name: 'Pending', value: pendingScans || 29, color: '#e5e7eb' }
  ];

  const totalProgress = progressData.reduce((sum, item) => sum + item.value, 0);
  const completedPercentage = totalProgress > 0 ? Math.round((progressData[0].value / totalProgress) * 100) : 41;

  return (
    <div className="donezo-dashboard-wrapper">
      {/* Main Dashboard Container */}
      <div className="donezo-dashboard-container">
        {/* Dashboard Header */}
        <div className="donezo-dashboard-header">
          <div className="donezo-header-left">
            <h1 className="donezo-dashboard-title">Dashboard</h1>
            <p className="donezo-dashboard-subtitle">Plan, prioritize, and accomplish your compliance tasks with ease.</p>
          </div>
          <div className="donezo-header-right">
            <button
              className="donezo-btn donezo-btn-primary"
              onClick={() => navigate(PAGEPATHS.StartAScan)}
            >
              <Plus size={18} />
              Add Project
            </button>
            <button
              className="donezo-btn donezo-btn-secondary"
              onClick={() => navigate(PAGEPATHS.Scanlist)}
            >
              Import Data
            </button>
          </div>
        </div>

        {/* Stats Cards Row */}
        <div className="donezo-stats-row">
          {statsData.map((stat, index) => (
            <div
              key={index}
              className={`donezo-stat-card ${stat.isPrimary ? 'donezo-stat-primary' : ''}`}
            >
              <div className="donezo-stat-header">
                <span className="donezo-stat-title">{stat.title}</span>
                <button className="donezo-stat-arrow">
                  <ArrowUpRight size={18} />
                </button>
              </div>
              <div className="donezo-stat-value">{stat.value}</div>
              <div className="donezo-stat-footer">
                <span className="donezo-stat-icon">{stat.icon}</span>
                <span className="donezo-stat-subtitle">{stat.subtitle}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Grid - 3 Columns */}
        <div className="donezo-main-grid">
          {/* Left Column - Largest */}
          <div className="donezo-column-left">
            {/* Project Analytics */}
            <div className="donezo-card">
              <h3 className="donezo-card-title">Project Analytics</h3>
              <div className="donezo-chart-wrapper">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={analyticsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis hide />
                    <Tooltip
                      cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                      contentStyle={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar
                      dataKey="value"
                      fill="#3b82f6"
                      radius={[8, 8, 8, 8]}
                      maxBarSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Team Collaboration */}
            <div className="donezo-card">
              <div className="donezo-card-header">
                <h3 className="donezo-card-title">Team Collaboration</h3>
                <button
                  className="donezo-add-btn"
                  onClick={() => navigate(PAGEPATHS.Usermanage)}
                >
                  + Add Member
                </button>
              </div>
              <div className="donezo-team-list">
                {teamMembers.map((member, index) => (
                  <div key={index} className="donezo-team-item">
                    <div className="donezo-team-left">
                      <div className="donezo-team-avatar">{member.avatar}</div>
                      <div className="donezo-team-info">
                        <div className="donezo-team-name">{member.name}</div>
                        <div className="donezo-team-task">Working on {member.task}</div>
                      </div>
                    </div>
                    <span className={`donezo-badge donezo-badge-${member.statusColor}`}>
                      {member.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Download Mobile App Card */}
            <div className="donezo-card donezo-card-dark">
              <div className="donezo-mobile-content">
                <div className="donezo-mobile-icon">📱</div>
                <h4 className="donezo-mobile-title">Download our Mobile App</h4>
                <p className="donezo-mobile-subtitle">Get easy in another way</p>
                <button className="donezo-btn donezo-btn-download">Download</button>
              </div>
              <div className="donezo-mobile-wave"></div>
            </div>
          </div>

          {/* Middle Column */}
          <div className="donezo-column-middle">
            {/* Reminders */}
            <div className="donezo-card">
              <div className="donezo-card-header">
                <h3 className="donezo-card-title">Reminders</h3>
                <button className="donezo-add-btn">+ New</button>
              </div>
              <div className="donezo-reminder-content">
                <h4 className="donezo-reminder-title">Meeting with Arc Company</h4>
                <p className="donezo-reminder-time">
                  <Calendar size={14} />
                  Time : 02:00 pm - 04:00 pm
                </p>
                <button
                  className="donezo-btn donezo-btn-meeting"
                  onClick={() => navigate(PAGEPATHS.Newdashboard)}
                >
                  <span className="donezo-meeting-icon">📹</span>
                  Start Meeting
                </button>
              </div>
            </div>

            {/* Project Progress */}
            <div className="donezo-card">
              <h3 className="donezo-card-title">Project Progress</h3>
              <div className="donezo-progress-wrapper">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={progressData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={75}
                      startAngle={90}
                      endAngle={450}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {progressData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="donezo-progress-center">
                  <div className="donezo-progress-percent">{completedPercentage}%</div>
                  <div className="donezo-progress-label">Project Ended</div>
                </div>
              </div>
              <div className="donezo-progress-legend">
                <div className="donezo-legend-item">
                  <span className="donezo-legend-dot" style={{ background: '#3b82f6' }}></span>
                  <span>Completed</span>
                </div>
                <div className="donezo-legend-item">
                  <span className="donezo-legend-dot" style={{ background: '#1d4ed8' }}></span>
                  <span>In Progress</span>
                </div>
                <div className="donezo-legend-item">
                  <span className="donezo-legend-dot" style={{ background: '#e5e7eb' }}></span>
                  <span>Pending</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="donezo-column-right">
            {/* Project List */}
            <div className="donezo-card">
              <div className="donezo-card-header">
                <h3 className="donezo-card-title">Project</h3>
                <button
                  className="donezo-add-btn"
                  onClick={() => navigate(PAGEPATHS.Scanlist)}
                >
                  + New
                </button>
              </div>
              <div className="donezo-projects-list">
                {recentProjects.length > 0 ? recentProjects.map((project, index) => (
                  <div
                    key={index}
                    className="donezo-project-item"
                    onClick={() => navigate(`${PAGEPATHS.Dashboard}/${project.scanId}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className={`donezo-project-icon donezo-icon-${project.color}`}>
                      {project.icon}
                    </span>
                    <div className="donezo-project-info">
                      <div className="donezo-project-name">{project.name}</div>
                      <div className="donezo-project-date">Due date: {project.dueDate}</div>
                    </div>
                  </div>
                )) : (
                  <div className="donezo-empty-state">No projects yet</div>
                )}
              </div>
            </div>

            {/* Time Tracker */}
            <div className="donezo-card donezo-card-timer">
              <h3 className="donezo-timer-title">Time Tracker</h3>
              <div className="donezo-timer-display">{formatTime(timerSeconds)}</div>
              <div className="donezo-timer-controls">
                <button
                  className="donezo-timer-btn"
                  onClick={() => setTimerRunning(!timerRunning)}
                >
                  {timerRunning ? <Pause size={24} fill="white" /> : <Play size={24} fill="white" />}
                </button>
                <button
                  className="donezo-timer-btn donezo-timer-stop"
                  onClick={() => {
                    setTimerRunning(false);
                    setTimerSeconds(0);
                  }}
                >
                  <Square size={24} fill="white" />
                </button>
              </div>
              <div className="donezo-timer-wave"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainDashboard;
